import { useRef } from "react";

/**
 * Refresh policy for the assets grid.
 *
 * Two decisions live here, both of which the grid container would otherwise
 * make inline at a dozen call sites.
 *
 * **Which page a refetch targets.** A refresh means "show me what I'm looking
 * at, updated" — it must not drop the user back on page 1 because a folder was
 * renamed or a generation task ticked over. Only a handful of actions, all of
 * them creating something new that lands at the top of the newest-first
 * ordering, want `refreshToFirstPage`.
 *
 * **Whether a pushed refetch runs at all.** Pushed updates — a task changing
 * state, an MCP tool touching the folder — re-sort the grid underneath the
 * user, so two things suppress them:
 *
 * 1. Past the first page. New items land at the top of the newest-first
 *    ordering, so page 3 gains nothing from the refetch and pays for it by
 *    having every card shift a slot. The user sees the new work the moment they
 *    page back — the grid is paginated server-side, so navigating refetches
 *    anyway.
 * 2. While a card holds an open inline editor. A refresh moves the card being
 *    typed into, and when it sits in the last slot it moves off the page
 *    entirely, taking the unsaved draft with it.
 *
 * A suppressed refresh is dropped, not queued. Committing the rename refetches
 * on its own (see `handleInlineRenameAsset`), so the grid catches up the moment
 * the edit lands.
 *
 * Refreshes the user directly asked for — their own rename, delete, move,
 * upload — call `refreshCurrentPage` and are never suppressed. They are the
 * result of an action just taken, so they always run immediately.
 *
 */
export function useHandleRefresh({ fetchItems, getCurrentPage }) {
  // Locks are keyed by item id rather than kept as a single boolean, so two
  // cards being edited at once release independently.
  const refreshLockHolderIdsRef = useRef(new Set());

  const refreshCurrentPage = async () => {
    await fetchItems(getCurrentPage());
  };

  const refreshToFirstPage = async () => {
    await fetchItems(1);
  };

  const refreshCurrentPageIfFirstPage = async () => {
    const onFirstPage = getCurrentPage() === 1;
    if (!onFirstPage) return;

    await refreshCurrentPage();
  };

  /**
   * The entry point for every pushed (not user-initiated) update.
   */
  const refreshUnlessLocked = async () => {
    const onFirstPage = getCurrentPage() === 1;
    if (!onFirstPage) return;

    const locked = refreshLockHolderIdsRef.current.size > 0;
    if (locked) return;

    await refreshCurrentPage();
  };

  /**
   * Handed straight to the grid cards. They report only whether they hold an
   * uncommitted edit; deciding that this should hold off a refresh is our
   * business, not theirs.
   *
   * @input {itemId: '671f…', editing: true}
   */
  const handleItemEditingChange = ({ itemId, editing }) => {
    const lockHolderIds = refreshLockHolderIdsRef.current;

    if (editing) {
      lockHolderIds.add(itemId);
      return;
    }

    lockHolderIds.delete(itemId);
  };

  /**
   * Called by every fetch with the ids the server just returned.
   *
   * A card that leaves the grid mid-edit — folder navigation, a filter change,
   * paging — never gets to report that it stopped editing, and one orphaned
   * lock would suppress every later refresh on page 1 for good. So each fetch
   * reconciles the held locks against what is actually on screen.
   *
   * @input new Set(['671f…', '6720…'])
   */
  const releaseLocksForItemsNoLongerOnGrid = (visibleItemIds) => {
    const lockHolderIds = refreshLockHolderIdsRef.current;

    lockHolderIds.forEach((lockId) => {
      if (visibleItemIds.has(lockId)) return;
      lockHolderIds.delete(lockId);
    });
  };

  return {
    refreshCurrentPage,
    refreshToFirstPage,
    refreshCurrentPageIfFirstPage,
    refreshUnlessLocked,
    handleItemEditingChange,
    releaseLocksForItemsNoLongerOnGrid,
  };
}
